import { Blanket, BlanketImage, BlanketInfo } from "@/components/core/blanket.tsx";
import { Searchbar } from "@/components/core/searchbar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs.tsx";
import { Content } from "@/components/layouts/content";
import { caseActivityPaymentQueryOptions } from "@/features/case-record/api/queries/case-activitiy-payment-query";
import {
	notificationsQueryOptions,
	unreadNotificationsQueryOptions,
	useReadNotificationMutation,
} from "@/features/dashboard/api/queries/notification-queries.ts";
import type { Notification as NotificationType } from "@/features/dashboard/types/notification.ts";
import { IconCalendarCheck, IconGavel, IconMoodLookDown, IconSettings } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { formatDistanceToNowStrict } from "date-fns";
import { useState } from "react";

export const Route = createFileRoute("/_dashboard/notifications")({
	loader: ({ context: { queryClient }, deps }) => {
		return queryClient.ensureQueryData(
			caseActivityPaymentQueryOptions({
				...deps,
				status: "pending",
			}),
		);
	},
	component: RouteComponent,
});

function NotificationItem({
	notification,
	onRead,
}: {
	notification: NotificationType;
	onRead: (id: string) => void;
}) {
	const isUnread = !notification.read_at;

	const formatDate = (dateString: string) => {
		return formatDistanceToNowStrict(new Date(dateString), { addSuffix: true });
	};

	return (
		<Link
			to={notification.data.attributes?.caseId ? `/case-evaluation/${notification.data.attributes?.caseId}?tabs=case-flow` : "/"}
			className="flex flex-row items-center w-full gap-2 px-4 py-2 hover:bg-slate-50"
			onClick={() => onRead(notification.id)}
		>
			<div
				className={`relative self-start inline-flex justify-center items-center shrink-0 w-[32px] h-[32px] mt-1 rounded-full
          ${isUnread ? "bg-blue-600" : "bg-slate-300"} text-white`}
			>
				{isUnread && (
					<div className="absolute -top-1 -right-1 self-start inline-flex shrink-0 w-[12px] h-[12px] p-[2px] mt-[4px] rounded-full bg-background">
						<div className="inline-flex w-full h-full rounded-full bg-green-600" />
					</div>
				)}
				{notification.data.attributes?.activity_type === "Hearing" ? (
					<IconCalendarCheck size={18} stroke={2} className="bg-teal-500 w-[32px] h-[32px] text-white p-1.5 rounded-full" />
				) : notification.category === "system" ? (
					<IconSettings size={18} stroke={1.5} />
				) : (
					<IconGavel size={18} stroke={1.5} />
				)}
			</div>
			<div className="flex flex-row items-start w-full gap-2">
				<div className="flex flex-col w-full">
					<div className="inline-flex flex-row items-center flex-wrap">
						<span className="text-base font-bold text-foreground-dark">{notification.data.title}</span>
					</div>
					{notification?.data.message && (
						<div
							className="notification-message text-base"
							// biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
							dangerouslySetInnerHTML={{
								__html: notification.data.message,
							}}
						/>
					)}
					<span className="text-base text-foreground/50 whitespace-nowrap">{formatDate(notification.created_at)}</span>
				</div>
			</div>
		</Link>
	);
}

function RouteComponent() {
	const [category, setCategory] = useState("");
	const unreadNotificationsQuery = useQuery(unreadNotificationsQueryOptions());
	const notificationQuery = useQuery(
		notificationsQueryOptions({
			category,
		}),
	);
	const readNotificationMutation = useReadNotificationMutation();
	const unreadNotifications = unreadNotificationsQuery.data;
	const notifications = notificationQuery.data;

	const onRead = (id: string, notification?: Notification) => {
		readNotificationMutation.mutate(id, {
			onSuccess: () => {
				notificationQuery.refetch();

				const url = notification?.data?.url;
				if (url) {
					window.location.href = url;
				}
			},
		});
	};

	return (
		<Content>
			<div className="flex flex-col w-full max-w-base p-6 gap-4">
				<div className="flex flex-col w-full">
					<span className="text-xl text-foreground-dark font-semibold">Notifications ({Number.parseInt(unreadNotifications?.unread_count)})</span>
					<span className="text-base text-foreground/70">Your collection of system related notifications</span>
				</div>
				<div className="flex flex-col w-full pt-4 rounded-md border border-slate-200">
					<Tabs
						onValueChange={async (value) => {
							setCategory(value);
						}}
						defaultValue={category}
						variant="compact"
					>
						<TabsList>
							<TabsTrigger value="">All</TabsTrigger>
							<TabsTrigger value="compliance">Compliance</TabsTrigger>
							<TabsTrigger value="system">System</TabsTrigger>
						</TabsList>
						<div className="flex flex-row w-full p-4 pb-2">
							<Searchbar />
						</div>
						<TabsContent value={category}>
							{notifications?.data.length !== 0 ? (
								<div className="flex flex-col w-full">
									{notifications?.data.slice(0, 20).map((notification) => (
										<NotificationItem key={notification.id} notification={notification} onRead={(value) => onRead(value, notification)} />
									))}
								</div>
							) : (
								<div className="flex flex-col w-full p-4">
									<Blanket width="sm">
										<BlanketImage icon={<IconMoodLookDown size={30} stroke={1.5} />} />
										<BlanketInfo title="No Data" message="No available data for for now. Please check back later or refresh the page." />
									</Blanket>
								</div>
							)}
						</TabsContent>
					</Tabs>
				</div>
			</div>
		</Content>
	);
}
